import { supabase } from '@/integrations/supabase/client';
import {
  PropostaComercial,
  CreatePropostaComercialDTO,
  UpdatePropostaComercialDTO,
  PropostaComercialFilters,
  PropostaPublica
} from '@/features/propostas/types/propostaComercial';

const parseSupabaseDate = (dateStr: string | null | undefined): Date | undefined => {
  if (!dateStr) return undefined;
  // Se for apenas data YYYY-MM-DD (tipo date do postgres)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr.replace(/-/g, '/'));
  }
  return new Date(dateStr);
};

const mapearPropostaComercial = (proposta: any): PropostaComercial => {
  return {
    id: proposta.id.toString(),
    uuid: proposta.uuid || '',
    titulo: proposta.titulo || '',
    cliente_nome: proposta.cliente_nome || '',
    consultor_id: proposta.consultor_id?.toString() || '',
    grupo_id: proposta.grupo_id?.toString() || '',
    bem_id: proposta.bem_id?.toString() || '',

    grupo: proposta.grupos ? {
      id: proposta.grupos.id.toString(),
      numero_grupo: proposta.grupos.numero_grupo || 0,
      especie: proposta.grupos.especie || '',
      duracao_total_meses: proposta.grupos.duracao_total_meses || 0,
      tipo_plano: proposta.grupos.tipo_plano || '',
      indice_reajuste: proposta.grupos.indice_reajuste || '',
    } : undefined,

    simulacao_lance: {
      valor_veiculo: Number(proposta.valor_veiculo || 0),
      percentual_lance: Number(proposta.percentual_lance || 0),
      percentual_carta: Number(proposta.percentual_carta || 0),
      percentual_bolso: Number(proposta.percentual_bolso || 0),
      taxa_adm_total: Number((proposta as any).taxa_adm_total || 0),
      valor_lance: Number(proposta.valor_lance || 0),
      valor_carta: Number(proposta.valor_carta || 0),
      valor_bolso: Number(proposta.valor_bolso || 0),
      valor_bolso_entrada: Number(proposta.valor_bolso_entrada || 0),
      valor_bolso_adicional: Number(proposta.valor_bolso_adicional || 0),
      percentual_entrada: Number(proposta.percentual_entrada || 0),
      percentual_adicional: Number(proposta.percentual_adicional || 0),
      valor_liquido_receber: Number(proposta.valor_liquido_receber || 0),
      nova_parcela_pos_contemplacao: Number(proposta.nova_parcela_pos_contemplacao || 0),
      novo_prazo: proposta.novo_prazo ? Number(proposta.novo_prazo) : undefined,
    },

    comparacao_financiamento: proposta.valor_veiculo_financiamento ? {
      valor_veiculo: Number(proposta.valor_veiculo_financiamento),
      entrada: Number(proposta.entrada_financiamento || 0),
      taxa_juros_mensal: Number(proposta.taxa_juros_mensal || 0),
      numero_parcelas: Number(proposta.numero_parcelas_financiamento || 0),
      valor_parcela_financiamento: Number(proposta.valor_parcela_financiamento || 0),
      custo_total_financiamento: Number(proposta.custo_total_financiamento || 0),
      custo_total_consorcio: Number(proposta.custo_total_consorcio || 0),
      economia_gerada: Number(proposta.economia_gerada || 0),
    } : undefined,

    status: proposta.status || 'RASCUNHO',
    data_criacao: new Date(proposta.created_at),
    data_atualizacao: new Date(proposta.updated_at || proposta.created_at),
    data_envio: parseSupabaseDate(proposta.data_envio),
    data_validade: parseSupabaseDate(proposta.data_validade),
    observacoes: proposta.observacoes || '',
    tenant_id: proposta.tenant_id?.toString() || '',

    configuracao_visual: proposta.cor_primaria ? {
      cor_primaria: proposta.cor_primaria,
      logo_url: proposta.logo_url,
      texto_personalizado: proposta.texto_personalizado,
    } : undefined,
  };
};

export class PropostaComercialSupabaseService {
  /**
   * Lista propostas comerciais com filtros
   */
  async listarPropostas(filtros?: PropostaComercialFilters, tenantId?: string): Promise<PropostaComercial[]> {
    try {
      let query = supabase
        .from('propostas_comerciais')
        .select(`
          *,
          grupos(*)
        `)
        .order('created_at', { ascending: false });

      // Aplicar filtro de tenant
      if (tenantId) {
        query = query.eq('tenant_id', parseInt(tenantId));
      }

      // Aplicar filtros
      if (filtros?.status) {
        query = query.eq('status', filtros.status);
      }
      if (filtros?.cliente_nome) {
        query = query.ilike('cliente_nome', `%${filtros.cliente_nome}%`);
      }
      if (filtros?.consultor_id) {
        query = query.eq('consultor_id', parseInt(filtros.consultor_id));
      }
      if (filtros?.grupo_id) {
        query = query.eq('grupo_id', parseInt(filtros.grupo_id));
      }
      if (filtros?.data_inicio) {
        query = query.gte('created_at', filtros.data_inicio.toISOString());
      }
      if (filtros?.data_fim) {
        query = query.lte('created_at', filtros.data_fim.toISOString());
      }
      if (filtros?.search) {
        query = query.or(`titulo.ilike.%${filtros.search}%,observacoes.ilike.%${filtros.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao listar propostas comerciais:', error);
        throw error;
      }

      // Mapear propostas e buscar contagem de interesses
      const propostas = data?.map(mapearPropostaComercial) || [];

      // Buscar contagem de interesses para cada proposta
      if (propostas.length > 0) {
        const propostaIds = propostas.map(p => parseInt(p.id));
        const { data: interessesData } = await supabase
          .from('propostas_interesses')
          .select('proposta_id')
          .in('proposta_id', propostaIds);

        // Contar interesses por proposta
        const interessesPorProposta = (interessesData || []).reduce((acc, item) => {
          acc[item.proposta_id] = (acc[item.proposta_id] || 0) + 1;
          return acc;
        }, {} as Record<number, number>);

        // Adicionar contagem às propostas
        propostas.forEach(proposta => {
          proposta.total_interesses = interessesPorProposta[parseInt(proposta.id)] || 0;
        });
      }

      return propostas;
    } catch (error) {
      console.error('Erro ao listar propostas comerciais:', error);
      throw error;
    }
  }

  /**
   * Busca uma proposta comercial específica
   */
  async buscarProposta(id: string): Promise<PropostaComercial> {
    try {
      const { data, error } = await supabase
        .from('propostas_comerciais')
        .select(`
          *,
          grupos(*),
          propostas_interesses(*)
        `)
        .eq('id', parseInt(id))
        .single();

      if (error) {
        console.error('Erro ao buscar proposta comercial:', error);
        throw error;
      }

      const proposta = mapearPropostaComercial(data);

      if (data.propostas_interesses) {
        proposta.interesses = data.propostas_interesses.map((pi: any) => ({
          id: pi.id.toString(),
          data_interesse: new Date(pi.data_interesse),
          visualizado: pi.visualizado,
          ip_address: pi.ip_address
        })).sort((a: any, b: any) => b.data_interesse.getTime() - a.data_interesse.getTime());

        proposta.total_interesses = proposta.interesses.length;
      }

      return proposta;
    } catch (error) {
      console.error('Erro ao buscar proposta comercial:', error);
      throw error;
    }
  }

  /**
   * Busca proposta por UUID (para link público).
   * RPC fail-closed: uma linha, só status publicável. Sem SELECT anônimo na tabela.
   */
  async buscarPropostaPorUuid(uuid: string): Promise<PropostaPublica> {
    try {
      const { data, error } = await supabase.rpc('buscar_proposta_publica', {
        p_uuid: uuid,
      });

      if (error) {
        console.error('Erro ao buscar proposta por UUID:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Proposta não encontrada');
      }

      const row = data as Record<string, any>;

      return {
        id: row.id.toString(),
        uuid: row.uuid || '',
        titulo: row.titulo || '',
        cliente_nome: row.cliente_nome || '',
        grupo_numero: row.grupos?.numero_grupo || 0,
        grupo_especie: row.grupos?.especie || '',
        simulacao_lance: {
          valor_lance: row.valor_lance || 0,
          valor_carta: row.valor_carta || 0,
          valor_bolso: row.valor_bolso || 0,
          valor_bolso_entrada: row.valor_bolso_entrada || 0,
          valor_bolso_adicional: row.valor_bolso_adicional || 0,
          valor_liquido_receber: row.valor_liquido_receber || 0,
          nova_parcela_pos_contemplacao: row.nova_parcela_pos_contemplacao || 0,
          taxa_adm_total: row.taxa_adm_total || 0,
          novo_prazo: row.novo_prazo,
        },
        comparacao_financiamento: row.valor_veiculo_financiamento ? {
          valor_parcela_financiamento: row.valor_parcela_financiamento || 0,
          custo_total_financiamento: row.custo_total_financiamento || 0,
          custo_total_consorcio: row.custo_total_consorcio || 0,
          economia_gerada: row.economia_gerada || 0,
        } : undefined,
        data_criacao: row.created_at ? new Date(row.created_at) : new Date(),
        data_validade: parseSupabaseDate(row.data_validade),
        observacoes: row.observacoes || '',
        configuracao_visual: row.cor_primaria ? {
          cor_primaria: row.cor_primaria,
          logo_url: row.logo_url || undefined,
          texto_personalizado: row.texto_personalizado || undefined,
        } : undefined,
        consultor: row.consultores ? {
          nome: row.consultores.nome || '',
          email: row.consultores.email || undefined,
          telefone: row.consultores.telefone || undefined,
        } : undefined,
      };
    } catch (error) {
      console.error('Erro ao buscar proposta por UUID:', error);
      throw error;
    }
  }

  /**
   * Cria uma nova proposta comercial
   */
  async criarProposta(propostaData: CreatePropostaComercialDTO): Promise<PropostaComercial> {
    try {
      // Gerar UUID único
      const uuid = crypto.randomUUID();

      // Buscar o consultor_id do usuário autenticado
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      // Buscar o consultor associado ao user_id
      const { data: consultor, error: consultorError } = await supabase
        .from('consultores')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (consultorError || !consultor) {
        console.error('Erro ao buscar consultor:', consultorError);
        throw new Error('Consultor não encontrado para o usuário autenticado');
      }

      // Usar os valores já calculados pelo frontend na simulação
      const simulacao = propostaData.simulacao_lance;

      // Usar os valores de comparação já calculados pelo frontend (se fornecidos)
      const comparacao = propostaData.comparacao_financiamento;

      // Buscar o perfil do usuário para pegar o tenant_id
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (profileError || !userProfile) {
        console.error('Erro ao buscar perfil:', profileError);
        throw new Error('Perfil não encontrado para o usuário autenticado');
      }

      const tenantId = userProfile.tenant_id;

      const { data, error } = await supabase
        .from('propostas_comerciais')
        .insert({
          uuid,
          titulo: propostaData.titulo,
          cliente_nome: propostaData.cliente_nome,
          consultor_id: consultor.id, // Usar o ID do consultor autenticado
          grupo_id: parseInt(propostaData.grupo_id),
          bem_id: propostaData.bem_id ? parseInt(propostaData.bem_id) : null,
          tenant_id: tenantId,
          status: 'FINALIZADA',

          // Simulação de lance
          valor_veiculo: simulacao.valor_veiculo,
          percentual_lance: simulacao.percentual_lance,
          percentual_carta: simulacao.percentual_carta,
          percentual_bolso: simulacao.percentual_bolso,
          taxa_adm_total: simulacao.taxa_adm_total,
          valor_lance: simulacao.valor_lance || 0,
          valor_carta: simulacao.valor_carta || simulacao.valor_veiculo || 0,
          valor_bolso: simulacao.valor_bolso || 0,
          valor_bolso_entrada: (simulacao as any).valor_bolso_entrada || 0,
          valor_bolso_adicional: (simulacao as any).valor_bolso_adicional || 0,
          percentual_entrada: (simulacao as any).percentual_entrada || 0,
          percentual_adicional: (simulacao as any).percentual_adicional || 0,
          valor_liquido_receber: simulacao.valor_liquido_receber || 0,
          nova_parcela_pos_contemplacao: simulacao.nova_parcela_pos_contemplacao || 0,
          novo_prazo: simulacao.novo_prazo,

          // Comparação com financiamento
          valor_veiculo_financiamento: comparacao?.valor_veiculo,
          entrada_financiamento: comparacao?.entrada,
          taxa_juros_mensal: comparacao?.taxa_juros_mensal,
          numero_parcelas_financiamento: comparacao?.numero_parcelas,
          valor_parcela_financiamento: comparacao?.valor_parcela_financiamento,
          custo_total_financiamento: comparacao?.custo_total_financiamento,
          custo_total_consorcio: comparacao?.custo_total_consorcio,
          economia_gerada: comparacao?.economia_gerada,

          observacoes: propostaData.observacoes,
          cor_primaria: propostaData.configuracao_visual?.cor_primaria,
          logo_url: propostaData.configuracao_visual?.logo_url,
          texto_personalizado: propostaData.configuracao_visual?.texto_personalizado,
        } as any)
        .select(`
          *,
          grupos(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao criar proposta comercial:', error);
        throw error;
      }

      return mapearPropostaComercial(data);
    } catch (error) {
      console.error('Erro ao criar proposta comercial:', error);
      throw error;
    }
  }

  /**
   * Atualiza uma proposta comercial existente
   */
  async atualizarProposta(id: string, propostaData: UpdatePropostaComercialDTO): Promise<PropostaComercial> {
    try {
      const updateData: any = {};

      if (propostaData.titulo !== undefined) updateData.titulo = propostaData.titulo;
      if (propostaData.cliente_nome !== undefined) updateData.cliente_nome = propostaData.cliente_nome;
      if (propostaData.grupo_id !== undefined) updateData.grupo_id = parseInt(propostaData.grupo_id);
      if (propostaData.bem_id !== undefined) updateData.bem_id = propostaData.bem_id ? parseInt(propostaData.bem_id) : null;
      if (propostaData.status !== undefined) updateData.status = propostaData.status;
      if (propostaData.data_validade !== undefined) updateData.data_validade = propostaData.data_validade ? propostaData.data_validade.toISOString().split('T')[0] : null;
      if (propostaData.observacoes !== undefined) updateData.observacoes = propostaData.observacoes;

      // Recalcular simulação se fornecida
      if (propostaData.simulacao_lance) {
        updateData.valor_veiculo = propostaData.simulacao_lance.valor_veiculo;
        updateData.percentual_lance = propostaData.simulacao_lance.percentual_lance;
        updateData.percentual_carta = propostaData.simulacao_lance.percentual_carta;
        updateData.percentual_bolso = propostaData.simulacao_lance.percentual_bolso;
        updateData.taxa_adm_total = propostaData.simulacao_lance.taxa_adm_total;
        
        // Usar os valores já calculados pelo frontend
        updateData.valor_lance = propostaData.simulacao_lance.valor_lance || 0;
        updateData.valor_carta = propostaData.simulacao_lance.valor_carta || propostaData.simulacao_lance.valor_veiculo || 0;
        updateData.valor_bolso = propostaData.simulacao_lance.valor_bolso || 0;
        updateData.valor_bolso_entrada = (propostaData.simulacao_lance as any).valor_bolso_entrada || 0;
        updateData.valor_bolso_adicional = (propostaData.simulacao_lance as any).valor_bolso_adicional || 0;
        updateData.percentual_entrada = (propostaData.simulacao_lance as any).percentual_entrada || 0;
        updateData.percentual_adicional = (propostaData.simulacao_lance as any).percentual_adicional || 0;
        updateData.valor_liquido_receber = propostaData.simulacao_lance.valor_liquido_receber || 0;
        updateData.nova_parcela_pos_contemplacao = propostaData.simulacao_lance.nova_parcela_pos_contemplacao || 0;
        updateData.novo_prazo = propostaData.simulacao_lance.novo_prazo;
      }

      // Recalcular comparação se fornecida
      if (propostaData.comparacao_financiamento) {
        // Obter os valores que vieram do front, não recalcular aqui porque o componente React já tem todas as taxas e cálculos complexos
        const custoTotalF = propostaData.comparacao_financiamento.custo_total_financiamento || 0;
        const custoTotalC = propostaData.comparacao_financiamento.custo_total_consorcio || 0;
        const economiaG = propostaData.comparacao_financiamento.economia_gerada || (custoTotalF - custoTotalC);

        updateData.valor_veiculo_financiamento = propostaData.comparacao_financiamento.valor_veiculo;
        updateData.entrada_financiamento = propostaData.comparacao_financiamento.entrada;
        updateData.taxa_juros_mensal = propostaData.comparacao_financiamento.taxa_juros_mensal;
        updateData.numero_parcelas_financiamento = propostaData.comparacao_financiamento.numero_parcelas;
        updateData.valor_parcela_financiamento = propostaData.comparacao_financiamento.valor_parcela_financiamento;
        updateData.custo_total_financiamento = custoTotalF;
        updateData.custo_total_consorcio = custoTotalC;
        updateData.economia_gerada = economiaG;
      }

      if (propostaData.configuracao_visual) {
        updateData.cor_primaria = propostaData.configuracao_visual.cor_primaria;
        updateData.logo_url = propostaData.configuracao_visual.logo_url;
        updateData.texto_personalizado = propostaData.configuracao_visual.texto_personalizado;
      }

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('propostas_comerciais')
        .update(updateData as any)
        .eq('id', parseInt(id))
        .select(`
          *,
          grupos(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao atualizar proposta comercial:', error);
        throw error;
      }

      return mapearPropostaComercial(data);
    } catch (error) {
      console.error('Erro ao atualizar proposta comercial:', error);
      throw error;
    }
  }

  /**
   * Exclui uma proposta comercial
   */
  async excluirProposta(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('propostas_comerciais')
        .delete()
        .eq('id', parseInt(id));

      if (error) {
        console.error('Erro ao excluir proposta comercial:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erro ao excluir proposta comercial:', error);
      throw error;
    }
  }

  /**
   * Atualiza status da proposta
   */
  async atualizarStatus(id: string, status: PropostaComercial['status']): Promise<PropostaComercial> {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (status === 'ENVIADA') {
        updateData.data_envio = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('propostas_comerciais')
        .update(updateData as any)
        .eq('id', parseInt(id))
        .select(`
          *,
          grupos(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao atualizar status:', error);
        throw error;
      }

      return mapearPropostaComercial(data);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      throw error;
    }
  }



  async registrarInteresse(uuid: string): Promise<void> {
    try {
      const { data, error } = await supabase.rpc('registrar_interesse_proposta', {
        p_uuid: uuid,
        p_user_agent: navigator.userAgent,
      });

      if (error) {
        console.error('Erro ao registrar interesse:', error);
        throw new Error('Erro ao registrar interesse');
      }

      if (!(data as any)?.success) {
        throw new Error('Falha ao registrar interesse');
      }
    } catch (error) {
      console.error('Erro ao registrar interesse:', error);
      throw error;
    }
  }
}
